import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2, Upload } from 'lucide-react';
import PermitsImportPanel from './PermitsImportPanel';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getPermitsList,
  savePermitRow,
  deletePermitRow,
  getActivitiesList,
  type PermitRow,
  type PermitType,
  type PermitStatus,
  type ActivityRow,
} from '@/services/db/dsrEntry';
import {
  FieldLabel,
  FieldError,
  ChipGroup,
  formatDateTimeLocal,
  fromDateTimeLocal,
} from './_shared';

const PERMIT_TYPES: { value: PermitType; label: string }[] = [
  { value: 'hot_work', label: 'Hot Work' },
  { value: 'confined_space_entry', label: 'Confined Space Entry' },
  { value: 'critical_lift', label: 'Critical Lift' },
  { value: 'electrical_isolation', label: 'Electrical Isolation' },
  { value: 'loto', label: 'LOTO' },
  { value: 'excavation', label: 'Excavation' },
  { value: 'overwater_work', label: 'Overwater Work' },
  { value: 'radiation', label: 'Radiation' },
  { value: 'general_work', label: 'General Work' },
  { value: 'other', label: 'Other' },
];

const PERMIT_STATUSES: { value: PermitStatus; label: string; color: string }[] = [
  { value: 'open', label: 'Open', color: '#00b4b4' },
  { value: 'active', label: 'Active', color: '#3ba870' },
  { value: 'suspended', label: 'Suspended', color: '#d87820' },
  { value: 'closed', label: 'Closed', color: '#9ca3af' },
  { value: 'cancelled', label: 'Cancelled', color: '#9ca3af' },
];

const entrySchema = z
  .object({
    permit_type: z.string().min(1, 'Permit type is required'),
    permit_number: z.string().min(1, 'Permit number is required'),
    status: z.string().min(1, 'Status is required'),
    issued_by: z.string().min(1, 'Issued by is required'),
    issued_at: z.string().min(1, 'Issue date/time is required'),
    expires_at: z.string().optional(),
    work_location: z.string().optional(),
    is_verified: z.boolean(),
    activity_entry_id: z.string().optional(),
    activity_description: z.string().optional(),
    comments: z.string().max(500, 'Max 500 characters').optional(),
  })
  .superRefine((data, ctx) => {
    if ((data.status === 'active' || data.status === 'open') && !data.expires_at?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Expiry date/time is required for Active or Open permits',
        path: ['expires_at'],
      });
    }
  });

type EntryFormValues = z.infer<typeof entrySchema>;

const EMPTY: EntryFormValues = {
  permit_type: 'general_work',
  permit_number: '',
  status: 'open',
  issued_by: '',
  issued_at: formatDateTimeLocal(new Date().toISOString()),
  expires_at: '',
  work_location: '',
  is_verified: false,
  activity_entry_id: '',
  activity_description: '',
  comments: '',
};

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
}

export default function PermitsForm({ dsrId, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'closed' | 'adding' | { editing: PermitRow }>('closed');
  const [showImport, setShowImport] = useState(false);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['entry_permits', dsrId],
    queryFn: () => getPermitsList(dsrId),
    staleTime: 30 * 1000,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['entry_activities', dsrId],
    queryFn: () => getActivitiesList(dsrId),
    staleTime: 30 * 1000,
  });

  const onSaved = () => {
    setMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_permits', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this permit?')) return;
    try {
      await deletePermitRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_permits', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete permit');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Permits to Work</h2>
        <p className="text-xs text-muted-foreground">
          Active and recent permits issued during this shift.
        </p>
      </div>

      {entries.length === 0 && mode === 'closed' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No permits yet. Add a permit below.
          </CardContent>
        </Card>
      )}

      {entries.map((entry) => (
        <PermitCard
          key={entry.id}
          entry={entry}
          onEdit={() => setMode({ editing: entry })}
          onDelete={() => onDelete(entry.id)}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isReadOnly && mode !== 'closed' && tenantId && (
        <PermitEntryForm
          dsrId={dsrId}
          tenantId={tenantId}
          activities={activities}
          editing={mode !== 'adding' ? mode.editing : undefined}
          onSaved={onSaved}
          onCancel={() => setMode('closed')}
        />
      )}

      {!isReadOnly && mode === 'closed' && showImport && tenantId && (
        <PermitsImportPanel
          dsrId={dsrId}
          tenantId={tenantId}
          onClose={() => setShowImport(false)}
          onImported={(count) => {
            setShowImport(false);
            queryClient.invalidateQueries({ queryKey: ['entry_permits', dsrId] });
            queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
            alert(`Imported ${count} permit${count === 1 ? '' : 's'}.`);
          }}
        />
      )}

      {!isReadOnly && mode === 'closed' && !showImport && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            className="flex-1 border-dashed border-mozaic-teal text-mozaic-teal"
            onClick={() => setMode('adding')}
          >
            <Plus className="mr-1 h-4 w-4" /> Add Permit
          </Button>
          <Button
            variant="outline"
            className="flex-1 border-dashed border-mozaic-teal text-mozaic-teal"
            onClick={() => setShowImport(true)}
          >
            <Upload className="mr-1 h-4 w-4" /> Import Permits
          </Button>
        </div>
      )}
    </div>
  );
}

function PermitCard({
  entry,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: PermitRow;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const typeLabel = PERMIT_TYPES.find((t) => t.value === entry.permit_type)?.label ?? entry.permit_type;
  const statusMeta = PERMIT_STATUSES.find((s) => s.value === entry.status);
  const expired =
    entry.expires_at &&
    new Date(entry.expires_at) < new Date() &&
    (entry.status === 'active' || entry.status === 'open');

  return (
    <Card className={expired ? 'border-mozaic-red' : undefined}>
      <CardContent className="pt-6">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-semibold text-mozaic-navy">{typeLabel}</h3>
            <p className="text-sm text-muted-foreground">{entry.permit_number}</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {statusMeta && (
              <Badge style={{ backgroundColor: statusMeta.color }} className="text-white">
                {statusMeta.label.toUpperCase()}
              </Badge>
            )}
            {entry.is_verified && (
              <Badge className="bg-mozaic-green text-white">VERIFIED</Badge>
            )}
          </div>
        </div>
        {entry.expires_at && (
          <p
            className={`text-xs ${
              expired ? 'font-semibold text-mozaic-red' : 'text-muted-foreground'
            }`}
          >
            Expires: {new Date(entry.expires_at).toLocaleString()}
            {expired && ' — EXPIRED'}
          </p>
        )}
        {entry.work_location && (
          <p className="mt-1 text-xs text-muted-foreground">{entry.work_location}</p>
        )}
        {!isReadOnly && (
          <div className="mt-3 flex gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PermitEntryForm({
  dsrId,
  tenantId,
  activities,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  activities: ActivityRow[];
  editing?: PermitRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const hasActivities = activities.length > 0;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: editing
      ? {
          permit_type: editing.permit_type,
          permit_number: editing.permit_number,
          status: editing.status,
          issued_by: editing.issued_by,
          issued_at: formatDateTimeLocal(editing.issued_at),
          expires_at: formatDateTimeLocal(editing.expires_at),
          work_location: editing.work_location ?? '',
          is_verified: editing.is_verified,
          activity_entry_id: editing.activity_entry_id ?? '',
          activity_description: editing.activity_description ?? '',
          comments: editing.comments ?? '',
        }
      : EMPTY,
  });

  const status = watch('status');

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await savePermitRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        permitType: values.permit_type as PermitType,
        permitNumber: values.permit_number,
        status: values.status as PermitStatus,
        issuedBy: values.issued_by,
        issuedAt: fromDateTimeLocal(values.issued_at),
        expiresAt: values.expires_at ? fromDateTimeLocal(values.expires_at) : null,
        workLocation: values.work_location || null,
        isVerified: values.is_verified,
        activityEntryId:
          hasActivities && values.activity_entry_id ? values.activity_entry_id : null,
        activityDescription:
          !hasActivities && values.activity_description ? values.activity_description : null,
        comments: values.comments || null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save permit');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">{editing ? 'Edit Permit' : 'New Permit'}</h3>

        <div className="space-y-2">
          <FieldLabel required>Permit Type</FieldLabel>
          <Controller
            control={control}
            name="permit_type"
            render={({ field }) => (
              <ChipGroup
                options={PERMIT_TYPES}
                value={field.value as PermitType}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.permit_type?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Permit Number / Reference</FieldLabel>
          <Controller
            control={control}
            name="permit_number"
            render={({ field }) => <Input {...field} placeholder="e.g. HW-2026-042" />}
          />
          <FieldError message={errors.permit_number?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Status</FieldLabel>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <ChipGroup
                options={PERMIT_STATUSES}
                value={field.value as PermitStatus}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.status?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Issued By</FieldLabel>
          <Controller
            control={control}
            name="issued_by"
            render={({ field }) => <Input {...field} placeholder="Name or role of issuer" />}
          />
          <FieldError message={errors.issued_by?.message} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel required>Issue Date / Time</FieldLabel>
            <Controller
              control={control}
              name="issued_at"
              render={({ field }) => (
                <Input {...field} type="datetime-local" />
              )}
            />
            <FieldError message={errors.issued_at?.message} />
          </div>
          <div className="space-y-2">
            <FieldLabel required={status === 'active' || status === 'open'}>
              Expiry Date / Time
            </FieldLabel>
            <Controller
              control={control}
              name="expires_at"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} type="datetime-local" />
              )}
            />
            <FieldError message={errors.expires_at?.message} />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel>Work Location</FieldLabel>
          <Controller
            control={control}
            name="work_location"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''} placeholder="e.g. Drill floor" />
            )}
          />
        </div>

        {hasActivities ? (
          <div className="space-y-2">
            <FieldLabel>Associated Activity</FieldLabel>
            <Controller
              control={control}
              name="activity_entry_id"
              render={({ field }) => (
                <ChipGroup
                  options={[
                    { value: '', label: 'None' },
                    ...activities.map((a) => ({
                      value: a.id,
                      label:
                        a.description.length > 30
                          ? a.description.slice(0, 30) + '…'
                          : a.description,
                    })),
                  ]}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v)}
                  wrap
                />
              )}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <FieldLabel>Associated Activity</FieldLabel>
            <Controller
              control={control}
              name="activity_description"
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  placeholder="Describe the associated activity"
                  maxLength={300}
                />
              )}
            />
          </div>
        )}

        <div className="space-y-2">
          <Controller
            control={control}
            name="is_verified"
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                SSR Verified
              </label>
            )}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel>Comments</FieldLabel>
          <Controller
            control={control}
            name="comments"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={2}
                maxLength={500}
                placeholder="Additional notes…"
              />
            )}
          />
          <FieldError message={errors.comments?.message} />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : editing ? (
              'Update Permit'
            ) : (
              'Save Permit'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
